/**
 * Vault and item envelopes.
 *
 * Each vault (personal or organization) has a random symmetric key
 * (`vaultKey`). Personal vault key is wrapped with account key; that
 * of an org vault is distributed per member (see `org.ts`).
 *
 * Each item stored as AES-256-GCM ENVELOPE whose AAD binds ciphertext to
 * identity: `boveda:item:v1:{vaultId}:{itemId}:{rev}`. Server cannot move
 * envelope to another item/vault or revert revision without AEAD check failing.
 * open falle.
 */

import { base64ToBytes, bytesToBase64, utf8Decode, utf8Encode } from "./encoding";
import { openBase64, randomSymmetricKey, sealBase64 } from "./primitives";
import type { ProtectedItem, VaultItemPlain } from "./types";

const AAD_PERSONAL_VAULT_KEY = "boveda:vault-key:v1";
const AAD_VAULT_NAME_PREFIX = "boveda:vault-name:v1:";

export function itemAad(vaultId: string, itemId: string, rev: number): string {
  return `boveda:item:v1:${vaultId}:${itemId}:${rev}`;
}

/**
 * Encrypts vault NAME to opaque envelope (`name_envelope`). AAD binds
 * envelope to its vault: server cannot reassign name to another vault without
 * open failing. Used in organization vaults (team name is
 * sensitive metadata); personal vault uses literal "Personal".
 */
export async function encryptVaultName(
  vaultKey: Uint8Array,
  vaultId: string,
  name: string,
): Promise<string> {
  return sealBase64(vaultKey, utf8Encode(name), AAD_VAULT_NAME_PREFIX + vaultId);
}

export async function decryptVaultName(
  vaultKey: Uint8Array,
  vaultId: string,
  envelope: string,
): Promise<string> {
  return utf8Decode(await openBase64(vaultKey, envelope, AAD_VAULT_NAME_PREFIX + vaultId));
}

/** Creates new vault key (32 random bytes). */
export function createVaultKey(): Uint8Array {
  return randomSymmetricKey();
}

/** Wraps PERSONAL vault key with account key. */
export async function wrapPersonalVaultKey(
  accountKey: Uint8Array,
  vaultKey: Uint8Array,
): Promise<string> {
  return sealBase64(accountKey, vaultKey, AAD_PERSONAL_VAULT_KEY);
}

export async function unwrapPersonalVaultKey(
  accountKey: Uint8Array,
  wrapped: string,
): Promise<Uint8Array> {
  return openBase64(accountKey, wrapped, AAD_PERSONAL_VAULT_KEY);
}

/** Encrypts plaintext item to `ProtectedItem` (opaque blob for server). */
export async function encryptItem(
  vaultKey: Uint8Array,
  vaultId: string,
  itemId: string,
  rev: number,
  plain: VaultItemPlain,
): Promise<ProtectedItem> {
  const bytes = utf8Encode(JSON.stringify(plain));
  const envelope = await sealBase64(vaultKey, bytes, itemAad(vaultId, itemId, rev));
  return { id: itemId, vaultId, envelope, rev };
}

export async function decryptItem(
  vaultKey: Uint8Array,
  item: ProtectedItem,
): Promise<VaultItemPlain> {
  const bytes = await openBase64(vaultKey, item.envelope, itemAad(item.vaultId, item.id, item.rev));
  // Trust boundary: envelope was sealed by THIS client (AEAD already authenticated content).
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  return JSON.parse(utf8Decode(bytes)) as VaultItemPlain;
}

/** Re-encrypts an item under new vault key (rotation) preserving id. */
export async function reencryptItem(
  oldVaultKey: Uint8Array,
  newVaultKey: Uint8Array,
  item: ProtectedItem,
): Promise<ProtectedItem> {
  const plain = await decryptItem(oldVaultKey, item);
  return encryptItem(newVaultKey, item.vaultId, item.id, item.rev + 1, plain);
}

export { base64ToBytes, bytesToBase64 };
